"use client";

import React, { useState, useEffect } from "react";
import { Menu, X, Globe } from "lucide-react";
import Image from "next/image";
import { useTranslations, useLocale } from "next-intl";
import { Link, usePathname, useRouter } from "@/i18n/navigation";
import { routing, type Locale } from "@/i18n/routing";

import { useAnalytics } from "@/components/analytics/use-analytics";
import TrackedWebAppLink from "./tracked-web-app-link";
import ThemeToggle from "./theme-toggle";

const LanguageSwitcher: React.FC = () => {
  const t = useTranslations("languageSwitcher");
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const { trackEvent } = useAnalytics();
  const [isOpen, setIsOpen] = useState(false);

  const languages = [
    { code: "en-US" as const, label: t("en-US") },
    { code: "pt-br" as const, label: t("pt-br") },
  ];

  const handleLanguageChange = (newLocale: string) => {
    trackEvent("language_changed", {
      from_locale: locale,
      to_locale: newLocale,
    });
    router.replace(pathname, { locale: newLocale as Locale });
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex min-h-11 min-w-11 items-center justify-center gap-1.5 text-sm font-medium text-foreground/80 transition-colors hover:text-primary lg:min-h-0 lg:min-w-0"
        aria-label={t("label")}
        aria-expanded={isOpen}
      >
        <Globe className="h-4 w-4" />
        <span className="hidden sm:inline">
          {locale === routing.defaultLocale ? "EN" : "PT"}
        </span>
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 mt-2 w-40 bg-popover rounded-lg shadow-lg border border-border py-1 z-50">
            {languages.map((lang) => (
              <button
                type="button"
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={`w-full px-4 py-3 text-left text-sm transition-colors hover:bg-accent ${
                  locale === lang.code
                    ? "text-primary font-medium bg-accent"
                    : "text-muted-foreground"
                }`}
              >
                {lang.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// `minimal` renders a logo-only bar (no nav, language/theme toggles or CTA) for
// focused funnel landings that must not offer an exit before the primary action.
const Header: React.FC<{ minimal?: boolean }> = ({ minimal = false }) => {
  const t = useTranslations("header");
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const logo = (
    <Link href="/" className="flex items-center" aria-label="BriefPanel home">
      <Image
        src="/logo-name.svg"
        alt="BriefPanel Logo"
        width={179}
        height={30}
        priority
        className="h-7 w-auto sm:h-8 lg:h-[30px] lg:w-[179px] dark:hidden"
      />
      <Image
        src="/logo-name-dark.svg"
        alt="BriefPanel Logo"
        width={179}
        height={30}
        priority
        className="hidden h-7 w-auto sm:h-8 lg:h-[30px] lg:w-[179px] dark:block"
      />
    </Link>
  );

  if (minimal) {
    return (
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-background/95 backdrop-blur shadow-md py-3"
            : "bg-transparent py-5"
        }`}
      >
        <div className="container mx-auto px-4 md:px-6 max-w-6xl">
          <div className="flex items-center">{logo}</div>
        </div>
      </header>
    );
  }

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-background/95 backdrop-blur shadow-md py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="container mx-auto px-4 md:px-6 max-w-6xl">
        <div className="flex items-center justify-between">
          <Link
            href="/"
            className="flex items-center"
            aria-label="BriefPanel home"
          >
            <Image
              src="/logo-name.svg"
              alt="BriefPanel Logo"
              width={179}
              height={30}
              priority
              className="h-6 w-auto sm:h-8 lg:h-[30px] lg:w-[179px] dark:hidden"
            />
            <Image
              src="/logo-name-dark.svg"
              alt="BriefPanel Logo"
              width={179}
              height={30}
              priority
              className="hidden h-6 w-auto sm:h-8 lg:h-[30px] lg:w-[179px] dark:block"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-5 xl:gap-8">
            <Link
              href="/#how-it-works"
              className="whitespace-nowrap text-sm font-medium transition-colors text-foreground/80 hover:text-primary"
            >
              {t("howItWorks")}
            </Link>
            <Link
              href="/pricing"
              className="whitespace-nowrap text-sm font-medium transition-colors text-foreground/80 hover:text-primary"
            >
              {t("pricing")}
            </Link>
            <Link
              href="/docs"
              className="whitespace-nowrap text-sm font-medium transition-colors text-foreground/80 hover:text-primary"
            >
              {t("docs")}
            </Link>
            <LanguageSwitcher />
            <ThemeToggle />
            <TrackedWebAppLink
              href="https://app.briefpanel.com"
              target="_blank"
              rel="noopener noreferrer"
              className="whitespace-nowrap bg-primary hover:bg-primary/90 text-primary-foreground px-5 py-2 rounded-full text-sm font-medium transition-colors shadow-md hover:shadow-lg shrink-0"
              location="header"
              label="Launch Web App"
            >
              {t("launchApp")}
            </TrackedWebAppLink>
          </nav>

          {/* Mobile Menu Button */}
          <div className="flex items-center gap-1 sm:gap-3 lg:hidden">
            <LanguageSwitcher />
            <ThemeToggle />
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-controls="mobile-navigation"
              aria-expanded={mobileMenuOpen}
              aria-label={t("menuLabel")}
              className="flex h-11 w-11 items-center justify-center text-foreground"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div
          id="mobile-navigation"
          className="lg:hidden bg-background border-b border-border shadow-lg px-4 py-6 absolute top-full left-0 right-0"
        >
          <nav className="flex flex-col space-y-1">
            <Link
              href="/#how-it-works"
              className="rounded-lg px-2 py-2.5 font-medium text-foreground hover:bg-muted hover:text-primary"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t("howItWorks")}
            </Link>
            <Link
              href="/pricing"
              className="rounded-lg px-2 py-2.5 font-medium text-foreground hover:bg-muted hover:text-primary"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t("pricing")}
            </Link>
            <Link
              href="/docs"
              className="rounded-lg px-2 py-2.5 font-medium text-foreground hover:bg-muted hover:text-primary"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t("docs")}
            </Link>
            <Link
              href="/blog"
              className="rounded-lg px-2 py-2.5 font-medium text-foreground hover:bg-muted hover:text-primary"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t("blog")}
            </Link>
            <TrackedWebAppLink
              href="https://app.briefpanel.com"
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-full bg-primary px-5 py-3 text-center font-medium text-primary-foreground shadow-md hover:bg-primary/90"
              onClick={() => setMobileMenuOpen(false)}
              location="mobile_header"
              label="Launch Web App"
            >
              {t("launchApp")}
            </TrackedWebAppLink>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
